import { isNotFound, isRedirect } from "@tanstack/react-router";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import { unwrapReadResult } from "@/lib/read-result";
import {
  isServer,
  queryOptions,
  type QueryClient,
} from "@tanstack/react-query";
import { actualViewerQueryOptions } from "@softmax/web-features/session/records";
import {
  getPlayActivity,
  getPlayEpisodes,
  getPlayRequests,
  getPlayShell,
} from "./play.functions";
export function prefetchPlayResource({
  client,
  href,
  resource,
}: {
  readonly client: QueryClient;
  readonly href: string;
  readonly resource: "activity" | "episodes" | "requests";
}) {
  if (isServer) return;

  void client.ensureQueryData(actualViewerQueryOptions()).then(
    (viewer) => {
      const viewerId =
        viewer.subject_type === "user" ? (viewer.subject_id ?? null) : null;

      switch (resource) {
        case "activity":
          return client.prefetchQuery(
            playActivityOptions({
              href,
              viewerId,
            }),
          );
        case "episodes":
          return client.prefetchQuery(
            playEpisodesOptions({
              href,
              viewerId,
            }),
          );
        case "requests":
          return client.prefetchQuery(
            playRequestsOptions({
              href,
              viewerId,
            }),
          );
      }
    },
    () => undefined,
  );
}
export function playShellOptions(href: string) {
  return queryOptions({
    queryKey: ["play", "shell", href],
    queryFn: ({ signal }) =>
      getPlayShell({ data: { href }, signal }).then(unwrapReadResult),
    retry: (count, error) =>
      !isNotFound(error) &&
      !isRedirect(error) &&
      !requestUnavailable(error) &&
      count < 1,
    staleTime: 60000,
  });
}
export function playActivityOptions({
  href,
  viewerId,
}: {
  readonly href: string;
  readonly viewerId: string | null;
}) {
  return queryOptions({
    queryKey: ["viewer", viewerId, "play", "activity", href],
    queryFn: ({ signal }) =>
      getPlayActivity({ data: { href }, signal }).then(unwrapReadResult),
    retry: (count, error) =>
      !isNotFound(error) &&
      !isRedirect(error) &&
      !requestUnavailable(error) &&
      count < 1,
    staleTime: 30000,
  });
}
export function playEpisodesOptions({
  href,
  viewerId,
}: {
  readonly href: string;
  readonly viewerId: string | null;
}) {
  return queryOptions({
    queryKey: ["viewer", viewerId, "play", "episodes", href],
    queryFn: ({ signal }) =>
      getPlayEpisodes({ data: { href }, signal }).then(unwrapReadResult),
    retry: (count, error) =>
      !isNotFound(error) &&
      !isRedirect(error) &&
      !requestUnavailable(error) &&
      count < 1,
    staleTime: 30000,
  });
}
export function playRequestsOptions({
  href,
  viewerId,
}: {
  readonly href: string;
  readonly viewerId: string | null;
}) {
  return queryOptions({
    queryKey: ["viewer", viewerId, "play", "requests", href],
    queryFn: ({ signal }) =>
      getPlayRequests({ data: { href }, signal }).then(unwrapReadResult),
    retry: (count, error) =>
      !isNotFound(error) &&
      !isRedirect(error) &&
      !requestUnavailable(error) &&
      count < 1,
    staleTime: 30000,
  });
}
